import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Heart, Flag } from 'lucide-react';
import { Challenge } from '../../types/challenges';
import { likeResult } from '../../lib/store';
import { auth } from '../../lib/firebase';
import { ReportContentModal } from '../moderation/ReportContentModal';

interface ChallengeResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: Challenge;
}

interface ImageState {
  loaded: boolean;
  error: boolean;
}

const RESULTS_PER_PAGE = 20;

export function ChallengeResultsModal({
  isOpen,
  onClose,
  challenge
}: ChallengeResultsModalProps) {
  const [displayedResults, setDisplayedResults] = useState<Challenge['participants']>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [imageStates, setImageStates] = useState<Record<string, ImageState>>({});
  const [likeLoading, setLikeLoading] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ challengeId: string; participantId: string } | null>(null);
  const observer = useRef<IntersectionObserver>();
  const lastResultRef = useRef<HTMLDivElement>(null);
  const imageObserver = useRef<IntersectionObserver>();

  const isAdmin = auth.currentUser?.email === 'breadflowbybadabec@gmail.com';

  // Filter and sort completed participants
  const completedParticipants = challenge.participants
    .filter(p => p.completed && p.result && (isAdmin || !p.result.hidden))
    .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());

  const loadMoreResults = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const currentLength = displayedResults.length;
      const nextResults = completedParticipants.slice(
        currentLength,
        currentLength + RESULTS_PER_PAGE
      );
      
      setDisplayedResults(prev => [...prev, ...nextResults]);
      setHasMore(currentLength + nextResults.length < completedParticipants.length);
      setLoading(false);
    }, 300);
  }, [completedParticipants, displayedResults.length]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setDisplayedResults(completedParticipants.slice(0, RESULTS_PER_PAGE));
      setHasMore(completedParticipants.length > RESULTS_PER_PAGE);
      setImageStates({});
    }
  }, [isOpen, challenge.id]);

  // Update displayed results when challenge data changes
  useEffect(() => {
    const updatedResults = completedParticipants.slice(0, displayedResults.length);
    setDisplayedResults(updatedResults);
  }, [challenge.participants]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreResults();
      }
    }, options);

    if (lastResultRef.current) {
      observer.current.observe(lastResultRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [loading, hasMore, loadMoreResults]);

  // Image lazy loading observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0
    };

    imageObserver.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.current?.unobserve(img);
          }
        }
      });
    }, options);

    return () => {
      if (imageObserver.current) {
        imageObserver.current.disconnect();
      }
    };
  }, []);

  const handleImageLoad = useCallback((userId: string) => {
    setImageStates(prev => ({
      ...prev,
      [userId]: { loaded: true, error: false }
    }));
  }, []);

  const handleImageError = useCallback((userId: string) => {
    setImageStates(prev => ({
      ...prev,
      [userId]: { loaded: true, error: true }
    }));
  }, []);

  const handleLike = async (e: React.MouseEvent, participantId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!auth.currentUser) return;
    
    try {
      setLikeLoading(participantId);
      await likeResult(challenge.id, participantId);
    } catch (error) {
      console.error('Error liking result:', error);
    } finally {
      setLikeLoading(null);
    }
  };

  const isLiked = (participant: Challenge['participants'][0]) => {
    return participant.result?.likedBy?.includes(auth.currentUser?.uid || '');
  };

  const handleReport = (e: React.MouseEvent, participantId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setReportTarget({ challengeId: challenge.id, participantId });
    setShowReportModal(true);
  };

  const handleReportSubmitted = () => {
    setReportTarget(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-white font-brand font-light italic">Challenge Results</h2>
              <p className="text-sm text-white/70 mt-1">
                {completedParticipants.length} bakers completed this challenge
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {displayedResults.length === 0 ? (
            <div className="text-center text-white/70 py-8">
              No results available yet
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {displayedResults.map((participant, index) => {
                const isLastResult = index === displayedResults.length - 1;
                const imageState = imageStates[participant.userId];
                const isCurrentlyLiked = isLiked(participant);
                
                return (
                  <div
                    key={participant.userId}
                    ref={isLastResult ? lastResultRef : null}
                    className="bg-white/10 rounded-xl overflow-hidden"
                  >
                    <div className="aspect-square relative">
                      {/* Loading placeholder */}
                      <div className={`absolute inset-0 bg-white/5 transition-opacity duration-300 ${
                        imageState?.loaded ? 'opacity-0' : 'opacity-100'
                      }`}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                        </div>
                      </div>

                      {/* Actual image */}
                      <img
                        data-src={participant.result!.imageUrl}
                        alt={`Challenge result by ${participant.name || 'Anonymous Baker'}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                          imageState?.loaded && !imageState?.error ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => handleImageLoad(participant.userId)}
                        onError={() => handleImageError(participant.userId)}
                        ref={img => {
                          if (img) {
                            imageObserver.current?.observe(img);
                          }
                        }}
                      />

                      {/* Error state */}
                      {imageState?.error && (
                        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                          Failed to load image
                        </div>
                      )}

                      {/* Like button and counter */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent flex justify-between items-center">
                        <button
                          onClick={(e) => handleLike(e, participant.userId)}
                          disabled={likeLoading === participant.userId}
                          className="flex items-center gap-2 text-white"
                        >
                          <Heart
                            className={`w-5 h-5 transition-all duration-300 ${
                              isCurrentlyLiked ? 'fill-red-500 text-red-500' : 'text-white hover:scale-110'
                            } ${likeLoading === participant.userId ? 'animate-pulse' : ''}`}
                          />
                          <span className="text-sm">
                            {participant.result?.likes || 0}
                          </span>
                        </button>

                        {participant.userId !== auth.currentUser?.uid && (
                          <button
                            onClick={(e) => handleReport(e, participant.userId)}
                            className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/20 rounded"
                            title="Report content"
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          )}
        </div>
      </div>

      {reportTarget && (
        <ReportContentModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportTarget(null);
          }}
          challengeId={reportTarget.challengeId}
          participantId={reportTarget.participantId}
          onReportSubmitted={handleReportSubmitted}
        />
      )}
    </div>
  );
}